<template>
    <div key="purchase-order" class="row w-100 h-100 mx-0 align-items-center">
        <div class="col-4 px-1 text-left">
            <!-- not found -->
            <div v-if="notMatchedQty === -1"
                 :key="`${lineIdx}-${type}-not-found`"
                 @click="selectItemLines"
                 class="is-not-matched-item"
                 v-tooltip="`${$lang.item.singular} not found`">
                <i class="fa fa-exclamation-triangle"></i>
            </div>
            <!-- not all matched -->
            <div v-else-if="notMatchedQty > 0"
                 :key="`${lineIdx}-${type}-not-matched-qty`"
                 @click="selectItemLines"
                 class="is-not-matched-item"
                 v-tooltip="`${getFormattedQty(notMatchedQty)} not matched`">
                <div class="d-inline-block position-relative">
                    <i class="far fa-caret-square-up fa-lg"></i>
                    <div class="count">{{ getFormattedQty(notMatchedQty) }}</div>
                </div>
            </div>
        </div>
        <!-- Partially matched -->
        <div class="col-4 px-1 text-center">
            <div v-if="lineQty && ![-1, 0].includes(notMatchedQty) && notMatchedQty < lineQty"
                 :key="`${lineIdx}-${type}-matched-partially`"
                 @click="viewMatched"
                 class="matched-item"
                 v-tooltip="`${lineQty - notMatchedQty} matched`">
                <i class="far fa-check fa-lg"></i>
            </div>
            <!-- All matched -->
            <div v-else-if="lineQty && notMatchedQty === 0"
                 :key="`${lineIdx}-${type}-matched-all`"
                 @click="viewMatched"
                 class="matched-item"
                 v-tooltip="`All matched`">
                <i class="far fa-check-double fa-lg"></i>
            </div>
        </div>
        <div class="col-4 px-1 text-right">
            <template v-for="(item, idx) in line.ordered">
                <div v-if="getItemPrice(item) === linePrice"
                     :key="`${lineIdx}-${idx}-${type}-item-price-equal`"
                     class="text-success d-inline-block px-1"
                     v-tooltip="`Matched ${$lang.item.singular} price ${getItemPriceFormatted(item)} equal to line ${$lang.item.singular} price ${linePriceFormatted}`">
                    <i class="far fa-equals fa-lg"></i>
                </div>
                <div v-else-if="getItemPrice(item) > linePrice"
                     :key="`${lineIdx}-${idx}-${type}-item-price-greater`"
                     class="text-warning d-inline-block px-1"
                     v-tooltip="`Matched ${$lang.item.singular} price ${getItemPriceFormatted(item)} greater than line ${$lang.item.singular} price ${linePriceFormatted}`">
                    <i class="far fa-greater-than fa-lg"></i>
                </div>
                <div v-else :key="`${lineIdx}-${idx}-${type}-item-price-less`"
                     class="text-warning d-inline-block px-1"
                     v-tooltip="`Matched ${$lang.item.singular} price ${getItemPriceFormatted(item)} less than line ${$lang.item.singular} price ${linePriceFormatted}`">
                    <i class="far fa-less-than fa-lg"></i>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import Vue from 'vue'
import {mapGetters} from 'vuex'
import {forceToNumber} from '@/utils'
import {MatchingExtensionTypes} from '@/types/document-viewer/extension/matching'
import {round} from 'lodash'

export default Vue.extend({
    name: 'AgGridCellRendererMatchingItem',
    data() {
        return {
            forceToNumber
        }
    },
    computed: {
        ...mapGetters('doc_viewer', [
            'lines',
            'priceAlertPropertyName'
        ]),

        ...mapGetters('doc_viewer_po', [
            'getNotMatchedPoQtyForLine'
        ]),

        ...mapGetters('doc_viewer_settings', [
            'getRoundingDecimalsByFieldPath'
        ]),

        ...mapGetters('doc_viewer_supplier_receipt', [
            'getNotMatchedDeliveryDocketQtyForLine'
        ]),

        type() {
            return this.params.colDef.fieldName === 'po'
                ? MatchingExtensionTypes.PURCHASE_ORDER
                : MatchingExtensionTypes.SUPPLIER_RECEIPT
        },

        isSupplierReceipt() {
            return this.type === MatchingExtensionTypes.SUPPLIER_RECEIPT
        },

        isPurchaseOrder() {
            return this.type === MatchingExtensionTypes.PURCHASE_ORDER
        },

        notMatchedQty() {
            return this.isPurchaseOrder
                ? this.getNotMatchedPoQtyForLine(this.line)
                : this.getNotMatchedDeliveryDocketQtyForLine(this.line)
        },

        displayValue() {
            return typeof this.params.colDef.valueFormatter === 'function'
                ? this.params.colDef.valueFormatter(this.params) : this.params.value
        },

        lineIdx() {
            return this.params.data ? this.params.data.lineIdx : -1
        },

        line() {
            return this.lines[this.lineIdx] || {}
        },

        lineQty() {
            return forceToNumber(this.line.qty)
        },

        linePrice() {
            return forceToNumber(this.line[this.priceAlertPropertyName])
        },

        linePriceFormatted() {
            return round(this.linePrice, this.getRoundingDecimalsByFieldPath('lines.price'))
        }
    },
    methods: {
        selectItemLines() {
            this.$modal.show('matching-item-select', {
                type: this.type,
                docLineMatchTo: _.cloneDeep(this.isSupplierReceipt ? this.line.receipts : this.line.ordered) || [],
                docLineIdx: this.lineIdx,
                searchQuery: this.line.sku
            })
        },

        viewMatched() {
            this.params.colDef.cellRendererParams.expandRow(this.params.rowIndex)
        },

        getItemPrice(item) {
            return forceToNumber(item.unitPrice)
        },

        getItemPriceFormatted(item) {
            return round(this.getItemPrice(item), this.getRoundingDecimalsByFieldPath('lines.price'))
        },

        getFormattedQty(qty) {
            return round(forceToNumber(qty), this.getRoundingDecimalsByFieldPath('lines.qty'))
        }
    }
})
</script>

<style scoped lang="scss">
.is-not-matched-item {
    color: var(--warning);

    &:hover {
        cursor: pointer;
    }

    .count {
        position: absolute;
        top: -6px;
        left: 16px;
        font-size: 12px;
        font-weight: 500;
    }
}

.matched-item {
    color: var(--success);

    &:hover {
        cursor: pointer;
    }
}
</style>
